import React from 'react';
import { Anchor, ArrowLeftRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';
import { centsToPrice } from '@/utils/formatter';
import { Currency, DEFAULT_CURRENCY } from '@/utils/price';
import {
  getCurrencySymbol,
  PRICE_FIELDS_BY_SYSTEM,
  type PriceFieldDef,
  type SystemType,
} from './constants';

export type PriceRawAccessors = {
  /** Raw storage value ("0.25" euro-units float or "1000" cents). */
  getRaw: (currency: string, def: PriceFieldDef) => string;
  setRaw: (currency: string, def: PriceFieldDef, raw: string) => void;
};

type ContainerPriceEditorProps = PriceRawAccessors & {
  systemType: SystemType;
  disabled: boolean;
  selectedCurrency: string;
  onSelectCurrency: (currency: string) => void;
};

/**
 * Decimal places a field allows: the transaction price keeps sub-cent
 * precision (3), every other field is a whole-cent money value (2).
 */
const decimalsFor = (def: PriceFieldDef): number =>
  def.scale === 'unitFloat' ? 3 : 2;

/**
 * Keep digits and a single dot, and cap the fractional part at `maxDecimals`
 * so the user can't type more precision than the field allows.
 */
const sanitizeDecimal = (text: string, maxDecimals: number): string => {
  const cleaned = (text || '').replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  const intPart = cleaned.slice(0, firstDot);
  const fracPart = cleaned
    .slice(firstDot + 1)
    .replace(/\./g, '')
    .slice(0, maxDecimals);
  return `${intPart}.${fracPart}`;
};

/**
 * Raw storage value → what the input shows (decimal currency units), always
 * padded to the field's precision (e.g. "5.00", or "0.025" for transaction).
 */
function rawToDisplay(def: PriceFieldDef, raw: string): string {
  if (raw == null || String(raw).trim() === '') return '';
  const units =
    def.scale === 'unitFloat'
      ? parseFloat(String(raw))
      : parseInt(String(raw), 10) / 100;
  if (Number.isNaN(units)) return '';
  return units.toFixed(decimalsFor(def));
}

/** Input text (decimal currency units) → raw storage value. */
function displayToRaw(def: PriceFieldDef, text: string): string {
  if (def.scale === 'unitFloat') return text;
  if (text.trim() === '') return '0';
  const units = parseFloat(text);
  if (Number.isNaN(units)) return '0';
  return String(Math.round(units * 100));
}

/** Format a raw EUR storage value as a localized price ("€0.25"). */
export function formatEurReference(def: PriceFieldDef, eurRaw: string): string {
  if (def.scale === 'unitFloat') {
    return centsToPrice(
      (parseFloat(eurRaw || '0') || 0) * 100,
      DEFAULT_CURRENCY,
      3,
    );
  }
  return centsToPrice(parseInt(eurRaw || '0', 10) || 0, DEFAULT_CURRENCY, 2);
}

type PriceFieldProps = {
  def: PriceFieldDef;
  currency: string;
  raw: string;
  eurRaw: string;
  isBaseCurrency: boolean;
  disabled: boolean;
  onCommit: (raw: string) => void;
};

/**
 * One labelled price input. While focused the field keeps its local text so
 * intermediate states like "3." survive; the committed value is written back
 * in storage scale on every change.
 */
function PriceField({
  def,
  currency,
  raw,
  eurRaw,
  isBaseCurrency,
  disabled,
  onCommit,
}: PriceFieldProps) {
  const [focusText, setFocusText] = React.useState<string | null>(null);
  const display = focusText ?? rawToDisplay(def, raw);

  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-xs tw:text-muted-foreground">
        {i18n.t(`EditContainerInfo:${def.labelKey}`)}
      </span>
      <div
        className={cn(
          'tw:flex tw:items-stretch tw:overflow-hidden tw:rounded-md tw:border tw:border-input tw:bg-background tw:shadow-xs',
          disabled && 'tw:opacity-60',
        )}
      >
        <span className="tw:inline-flex tw:items-center tw:pl-2.5 tw:pr-1 tw:font-mono tw:text-[13px] tw:text-muted-foreground">
          {getCurrencySymbol(currency)}
        </span>
        <input
          value={display}
          disabled={disabled}
          inputMode="decimal"
          className="tw:h-9 tw:min-w-0 tw:flex-1 tw:border-none tw:bg-transparent tw:pl-1 tw:pr-2.5 tw:text-right tw:font-mono tw:text-sm tw:text-foreground tw:outline-none"
          onFocus={() => setFocusText(rawToDisplay(def, raw))}
          onBlur={() => setFocusText(null)}
          onChange={(e) => {
            const text = sanitizeDecimal(e.target.value, decimalsFor(def));
            setFocusText(text);
            onCommit(displayToRaw(def, text));
          }}
        />
      </div>
      {!isBaseCurrency && (
        <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:text-[11.5px] tw:text-muted-foreground">
          <span
            className="tw:inline-flex tw:size-[7px] tw:flex-none tw:rounded-[2px]"
            style={{
              backgroundColor:
                'color-mix(in oklab, var(--color-success-main) 55%, var(--background))',
            }}
          />
          {i18n.t('EditContainerInfo:eurReference', {
            value: formatEurReference(def, eurRaw),
          })}
        </span>
      )}
    </div>
  );
}

/**
 * Currency-first price editor (replaces the EditContainerPrices grid for one
 * container): pick a currency chip, edit labelled fields, see the EUR
 * reference under each field while editing any other currency.
 */
export default function ContainerPriceEditor({
  systemType,
  disabled,
  selectedCurrency,
  onSelectCurrency,
  getRaw,
  setRaw,
}: ContainerPriceEditorProps) {
  const fields = PRICE_FIELDS_BY_SYSTEM[systemType];
  const isBaseCurrency = selectedCurrency === DEFAULT_CURRENCY;

  return (
    <div className="tw:flex tw:flex-col tw:gap-[18px] tw:px-5 tw:py-5">
      <div className="tw:flex tw:flex-col tw:gap-2">
        <span className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
          {i18n.t('EditContainerInfo:currencyEditing')}
        </span>
        <div className="tw:flex tw:flex-wrap tw:gap-1.5">
          {Object.keys(Currency).map((code) => {
            const selected = code === selectedCurrency;
            const isBase = code === DEFAULT_CURRENCY;
            return (
              <button
                key={code}
                type="button"
                onClick={() => onSelectCurrency(code)}
                className={cn(
                  'tw:inline-flex tw:h-8 tw:items-center tw:gap-1.5 tw:rounded-md tw:border tw:px-3 tw:font-mono tw:text-[12.5px] tw:font-semibold tw:tracking-wide tw:transition-colors tw:cursor-pointer',
                  selected
                    ? 'tw:border-primary tw:bg-primary tw:text-primary-foreground'
                    : 'tw:border-border tw:bg-background tw:text-foreground tw:hover:bg-accent',
                )}
              >
                {code}
                {isBase && (
                  <span
                    className={cn(
                      'tw:inline-flex tw:h-[15px] tw:items-center tw:rounded-full tw:px-1.5 tw:text-[9px] tw:font-bold tw:uppercase tw:tracking-wide',
                    )}
                    style={
                      selected
                        ? {
                            backgroundColor:
                              'color-mix(in oklab, var(--primary-foreground) 22%, transparent)',
                            color: 'var(--primary-foreground)',
                          }
                        : {
                            backgroundColor:
                              'color-mix(in oklab, var(--color-success-main) 16%, var(--background))',
                            color: '#0f7a52',
                          }
                    }
                  >
                    {i18n.t('EditContainerInfo:refBadge')}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="tw:flex tw:items-center tw:gap-2.5 tw:rounded-md tw:border tw:px-3.5 tw:py-2.5"
        style={
          isBaseCurrency
            ? {
                backgroundColor:
                  'color-mix(in oklab, var(--color-success-main) 8%, var(--background))',
                borderColor:
                  'color-mix(in oklab, var(--color-success-main) 26%, transparent)',
              }
            : {
                backgroundColor: 'var(--muted)',
                borderColor: 'var(--border)',
              }
        }
      >
        <span
          className="tw:inline-flex tw:flex-none"
          style={{
            color: isBaseCurrency ? '#0f7a52' : 'var(--muted-foreground)',
          }}
        >
          {isBaseCurrency ? <Anchor size={16} /> : <ArrowLeftRight size={16} />}
        </span>
        <span className="tw:text-[12.5px] tw:leading-snug tw:text-foreground">
          {isBaseCurrency
            ? i18n.t('EditContainerInfo:eurBannerBase')
            : i18n.t('EditContainerInfo:eurBannerOther', {
                currency: selectedCurrency,
              })}
        </span>
      </div>

      <div className="tw:grid tw:grid-cols-[repeat(auto-fit,minmax(150px,1fr))] tw:gap-3.5">
        {fields.map((def) => (
          <PriceField
            key={def.key}
            def={def}
            currency={selectedCurrency}
            raw={getRaw(selectedCurrency, def)}
            eurRaw={getRaw(DEFAULT_CURRENCY, def)}
            isBaseCurrency={isBaseCurrency}
            disabled={disabled}
            onCommit={(raw) => setRaw(selectedCurrency, def, raw)}
          />
        ))}
      </div>
    </div>
  );
}
